import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import BotListItem from '../botListItem/BotListItem';
import classes from './BotListPanel.module.css';

const SCROLLBAR_WIDTH = 6;
const MIN_THUMB_HEIGHT = 40;

export default function BotListPanel({ bots = [], activeRoutePath }) {
  const { texts } = useSelector((state) => state.translate);
  const listRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [thumb, setThumb] = useState({ visible: false, top: 0, height: 0 });

  const updateScrollUi = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setCanScrollUp(false);
      setCanScrollDown(false);
      setThumb({ visible: false, top: 0, height: 0 });
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    setCanScrollUp(scrollTop > 1);
    setCanScrollDown(maxScroll > 1 && scrollTop < maxScroll - 1);

    if (maxScroll <= 1) {
      setThumb({ visible: false, top: 0, height: 0 });
      return;
    }

    const trackHeight = clientHeight;
    const height = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
    const top = (scrollTop / maxScroll) * (trackHeight - height);
    setThumb({ visible: true, top, height });
  }, []);

  useEffect(() => {
    updateScrollUi();
  }, [bots, updateScrollUi]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => updateScrollUi());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollUi]);

  return (
    <aside className={classes.Panel}>
      <h2 className={classes.Title}>{texts.allMyBots}</h2>
      {canScrollUp ?
        <div className={classes.FadeTop} aria-hidden="true" />
        : null}
      <div className={classes.ListScrollWrap}>
        <div
          ref={listRef}
          className={classes.ListScroll}
          onScroll={updateScrollUi}
        >
          <ul className={classes.List}>
            {bots.map(({ projectKey, botName, routePath, headerBotName, subjectName, iconUrl, subjectIconUrl, approvedByPmr }, index) => {
              const isActive = (routePath || `${projectKey}/${botName}`) === activeRoutePath;
              const isLast = index === bots.length - 1;
              const showDivider = !isActive && !isLast;
              return (
                <li key={routePath || `${projectKey}/${botName}`} className={classes.ListItem}>
                  <BotListItem
                    projectKey={projectKey}
                    botName={botName}
                    routePath={routePath}
                    headerBotName={headerBotName}
                    subjectName={subjectName}
                    iconUrl={iconUrl}
                    subjectIconUrl={subjectIconUrl}
                    approvedByPmr={approvedByPmr}
                    isActive={isActive}
                    showDivider={showDivider}
                  />
                </li>
              );
            })}
          </ul>
        </div>
        {thumb.visible ?
          <div
            className={classes.ScrollbarThumb}
            style={{
              width: SCROLLBAR_WIDTH,
              height: thumb.height,
              top: thumb.top,
            }}
            aria-hidden="true"
          />
          : null}
      </div>
      {canScrollDown ?
        <div className={classes.FadeBottom} aria-hidden="true" />
        : null}
    </aside>
  );
}
